/**
 * Workspaces Page
 *
 * Account-level admin surface for browsing, creating, and editing the
 * tenant's workspaces.
 *
 * Design language matches the rest of the admin shell:
 *   • Top action bar with title + workspace count + gradient "New
 *     Workspace" CTA (the FAB is gone — a primary action this important
 *     shouldn't be hidden in a corner).
 *   • Per-row identity strip: brand color swatch + logo (or initial
 *     avatar fallback) + name + "created N ago" caption. Makes a long
 *     list of tenants visually scannable instead of a stack of names.
 *   • Quiet "view as workspace" picker — admin can preview branding
 *     without it dominating the page.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useLayoutConfig } from '../contexts/LayoutContext';
import { Avatar, Box, Button, Chip, IconButton, InputAdornment, ListItemIcon, ListItemText, Menu, MenuItem, TextField, Tooltip, Typography } from '../components/ui';
import { AddIcon, BarChartIcon, BookmarkIcon, EditIcon, MoreVertIcon, SearchIcon, VisibilityIcon, WorkspacesIcon } from '../components/icons';
import EditWorkspaceDialog from '../components/workspaces/EditWorkspaceDialog';
import PageHeader from '../components/common/PageHeader';
import PagedTable, { PagedTableColumn, SortDirection } from '../components/common/PagedTable';
import { usePagedList } from '../hooks/usePagedList';
import { workspacesClient } from '../apis/client-instances';
import { Workspace } from '../apis/api-client';
import { alert, getFileUrl } from '../hooks/function';
import { impersonateWorkspaceAdmin } from '../utils/impersonation';
import { tierToAccountType } from '../utils/workspaceTier';
import { RootState } from '../types';

// Tiny pre-fetcher: workspace logos live behind SAS URLs. Cache the
// resolved displayable URL by workspaceId so a row re-render doesn't
// re-fetch from the storage helper.
function useWorkspaceLogos(workspaces: Workspace[]) {
  const [urls, setUrls] = useState<Record<number, string>>({});

  useEffect(() => {
    let cancelled = false;
    const toLoad = workspaces.filter(
      (w) => w.id && w.logoUrl && !urls[w.id],
    );
    if (toLoad.length === 0) return;

    Promise.all(
      toLoad.map(async (w) => {
        const resolved = await getFileUrl(w.logoUrl!);
        return [w.id!, resolved || ''] as const;
      }),
    ).then((pairs) => {
      if (cancelled) return;
      setUrls((prev) => {
        const next = { ...prev };
        for (const [id, url] of pairs) if (url) next[id] = url;
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaces.map((w) => `${w.id}:${w.logoUrl}`).join('|')]);

  return urls;
}

export default function WorkspacesPage() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { setLayoutConfig } = useLayoutConfig();

  // Hide the topbar's course-scoped global search — workspace admin has
  // nothing to do with the public catalog.
  useEffect(() => {
    setLayoutConfig({ showBackButton: false, hideGlobalSearch: true });
    return () => setLayoutConfig({ hideGlobalSearch: false });
  }, [setLayoutConfig]);

  // Column sort — resolved server-side; changing either value refetches
  // from page 1. Keys ('name' | 'learners' | 'accountType') pass straight
  // through to searchWorkspaces, which orders the full result set.
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortDir, setSortDir] = useState<SortDirection>('asc');

  const paged = usePagedList<Workspace>({
    fetcher: useCallback(
      async ({ page, pageSize, search }) => {
        const result = await workspacesClient.searchWorkspaces(
          search || undefined,
          page,
          pageSize,
          undefined,
          sortBy,
          sortDir,
        );
        return {
          items: result.items ?? [],
          totalCount: result.totalCount ?? 0,
          page: result.page ?? page,
          pageSize: result.pageSize ?? pageSize,
        };
      },
      [sortBy, sortDir],
    ),
    deps: [sortBy, sortDir],
    onError: () => alert(enqueueSnackbar, 'Failed to load workspaces', 'error'),
  });

  const logoUrls = useWorkspaceLogos(paged.items);

  const [dialogOpen, setDialogOpen] = useState(false);
  // Row action menu — single kebab per row, opens a Menu with Edit /
  // View / Assign / Report. Cleaner than the icon strip we had and
  // matches the pattern on the AdminCourseTable.
  const [rowMenu, setRowMenu] = useState<{ anchorEl: HTMLElement; row: Workspace } | null>(null);
  const closeRowMenu = () => setRowMenu(null);

  const handleEdit = (workspace: Workspace) => navigate(`/workspaces/${workspace.id}`);
  const handleAdd = () => setDialogOpen(true);

  // "View as Workspace Admin" impersonates one of the workspace's real
  // Workspace Admins, so the preview is exactly what they'd see.
  const handleViewAs = (workspace: Workspace) => {
    if (!workspace.id) return;
    impersonateWorkspaceAdmin(workspace.id, '/workspaces').catch((err) =>
      alert(enqueueSnackbar, err?.message || 'Failed to impersonate', 'error'),
    );
  };

  // Two-letter initials fallback for workspaces with no logo. Strips
  // common admin-noise words so "Affinity Dental Management" → "AD".
  const initialsFor = (name?: string): string => {
    if (!name) return '?';
    const words = name
      .replace(/\b(dental|partners?|management|llc|inc)\b/gi, '')
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (words.length === 0) return name.slice(0, 2).toUpperCase();
    return (words[0][0] + (words[1]?.[0] || '')).toUpperCase();
  };

  const columns: PagedTableColumn<Workspace>[] = [
    {
      key: 'name',
      label: 'Workspace',
      sortable: true,
      render: (w) => {
        const color = w.primaryColor || '#cbd5e1';
        const logoUrl = w.id ? logoUrls[w.id] : undefined;
        const isDeactivated = w.deactivatedAt != null;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, opacity: isDeactivated ? 0.65 : 1 }}>
            {/* Brand color rail — slim left stripe that reads as a
                visual fingerprint at a glance. */}
            <Box
              sx={{
                width: 4,
                height: 36,
                borderRadius: 4,
                bgcolor: color,
                flexShrink: 0,
              }}
            />
            {/* Logo if available, otherwise initials avatar tinted with
                the workspace's brand color. */}
            <Avatar
              src={logoUrl}
              alt={w.name || ''}
              sx={{
                width: 36,
                height: 36,
                bgcolor: logoUrl ? 'transparent' : `${color}22`,
                color,
                fontWeight: 700,
                fontSize: '0.85rem',
                border: '1px solid',
                borderColor: 'divider',
                filter: isDeactivated ? 'grayscale(1)' : undefined,
              }}
              variant="rounded"
            >
              {!logoUrl && initialsFor(w.name)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {w.name || 'Untitled workspace'}
                </Typography>
                {isDeactivated && (
                  // Pastel "Deactivated" pill — same family as the User
                  // page's status chips so the cascade reads as one
                  // visual concept across the admin.
                  <Chip
                    label="Deactivated"
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      letterSpacing: '0.02em',
                      bgcolor: 'error.softBg',
                      color: 'error.softText',
                      '& .MuiChip-label': { px: 0.85 },
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        );
      },
    },
    {
      key: 'groups',
      label: 'Groups',
      width: 100,
      align: 'center',
      // Total group count for this workspace — returned inline on the
      // workspace search row (see WorkspaceRepo.PopulateListMetricsAsync).
      render: (w) => (
        <Typography variant="body2" fontWeight={600}>
          {(w.groupCount ?? 0).toLocaleString()}
        </Typography>
      ),
    },
    {
      key: 'admins',
      label: 'Admins',
      width: 100,
      align: 'center',
      // Active admin headcount — anyone whose role is Admin,
      // AccountAdmin, or WorkspaceAdmin (we lump them together because
      // from a tenant-management glance "how many managers do they
      // have" is the relevant question, not which flavor). Returned
      // inline on the workspace search row.
      render: (w) => (
        <Typography variant="body2" fontWeight={600}>
          {(w.activeAdminCount ?? 0).toLocaleString()}
        </Typography>
      ),
    },
    {
      key: 'learners',
      label: 'Learners',
      width: 130,
      align: 'center',
      sortable: true,
      // Active learner count vs. the admin-set seat limit (e.g. 390 / 400)
      // when a limit is configured; falls back to just the raw count
      // when no limit is set. Seat limit rides the workspace search row
      // (`w.seatLimit`). When the count exceeds the limit the figure
      // tints amber — sales/ops signal that the workspace is at capacity
      // and a tier change may be warranted.
      render: (w) => {
        const activeLearners = w.activeLearnerCount ?? 0;
        const seatLimit = w.seatLimit ?? null;
        const overLimit = seatLimit != null && activeLearners > seatLimit;
        return (
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ color: overLimit ? '#a06200' : 'text.primary' }}
          >
            {activeLearners.toLocaleString()}
            {seatLimit != null && (
              <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                {' / '}
                {seatLimit.toLocaleString()}
              </Box>
            )}
          </Typography>
        );
      },
    },
    {
      key: 'accountType',
      label: 'Account Type',
      width: 150,
      align: 'center',
      sortable: true,
      render: (w) => {
        const accountType = tierToAccountType(w.tier);
        // Three-way palette: Enterprise reads as the "paid premium"
        // primary cyan; Demo reads as a non-paying warning amber so
        // sales can spot trials; Standard sits in neutral grey.
        const style =
          accountType === 'enterprise'
            ? { label: 'Enterprise', bgcolor: 'rgba(var(--brand-rgb), 0.18)', color: 'primary.main' }
            : accountType === 'demo'
              ? { label: 'Demo', bgcolor: 'warning.softBg', color: 'warning.softText' }
              : { label: 'Standard', bgcolor: 'grey.100', color: 'text.secondary' };
        return (
          <Chip
            label={style.label}
            size="small"
            sx={{
              height: 22,
              fontSize: '0.7rem',
              fontWeight: 700,
              letterSpacing: '0.02em',
              bgcolor: style.bgcolor,
              color: style.color,
              '& .MuiChip-label': { px: 1 },
            }}
          />
        );
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      align: 'center',
      width: 80,
      // Single kebab — opens the actions Menu rendered below the
      // PagedTable. Destructive actions (deactivate / delete) stay
      // inside the workspace edit page, not on this list.
      render: (w) => (
        <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'inline-flex' }}>
          <Tooltip title="Actions">
            <IconButton size="small" onClick={(e) => setRowMenu({ anchorEl: e.currentTarget, row: w })}>
              <MoreVertIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'white' }}>
      <Box sx={{ flexGrow: 1 }}>
        <Box sx={{ p: 4 }}>
          <PageHeader
            icon={<WorkspacesIcon />}
            title="Workspaces"
            description="Manage tenants, their branding, and assignment scope."
            count={paged.totalCount}
            actions={
              <>
                <TextField
                  size="small"
                  placeholder="Search workspaces..."
                  value={paged.search ?? ''}
                  onChange={(e) => paged.setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: 280 }}
                />
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<AddIcon />}
                  onClick={handleAdd}
                  sx={{ px: 3 }}
                >
                  New Workspace
                </Button>
              </>
            }
          />

          {/* ── Workspace table — search is moved up into the header so
                we don't pass `onSearchChange` and PagedTable skips its
                internal search input. */}
          <PagedTable<Workspace>
            columns={columns}
            rows={paged.items}
            totalCount={paged.totalCount}
            page={paged.page}
            pageSize={paged.pageSize}
            loading={paged.loading}
            rowKey={(w) => w.id ?? w.name ?? ''}
            sortBy={sortBy}
            sortDir={sortDir}
            onSortChange={(key, dir) => {
              setSortBy(key);
              setSortDir(dir);
            }}
            onPageChange={paged.setPage}
            onPageSizeChange={paged.setPageSize}
            onRowClick={handleEdit}
            emptyMessage="No workspaces yet — click New Workspace to add one."
          />
        </Box>

        <EditWorkspaceDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSuccess={() => paged.refresh()}
        />

        {/* Row actions menu — anchored to the kebab clicked above.
            Single source of truth for the four per-row destinations
            (Edit settings, impersonate a workspace admin,
            jump to the workspace's Assignments tab, jump to Reports
            filtered to this workspace). */}
        <Menu
          anchorEl={rowMenu?.anchorEl ?? null}
          open={!!rowMenu}
          onClose={closeRowMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { minWidth: 200, borderRadius: 1.5 } }}
        >
          <MenuItem
            onClick={() => {
              if (rowMenu) handleEdit(rowMenu.row);
              closeRowMenu();
            }}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (rowMenu) handleViewAs(rowMenu.row);
              closeRowMenu();
            }}
          >
            <ListItemIcon>
              <VisibilityIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>View</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (rowMenu?.row?.id) {
                // Land on the workspace edit page's Assignments tab.
                navigate(`/workspaces/${rowMenu.row.id}?tab=assignments`);
              }
              closeRowMenu();
            }}
          >
            <ListItemIcon>
              <BookmarkIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Assign</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (rowMenu?.row?.id) {
                // Reports page's Activity tab already filters by
                // `workspaceId` URL param — opens scoped to this tenant.
                navigate(`/reports?tab=activity&workspaceId=${rowMenu.row.id}`);
              }
              closeRowMenu();
            }}
          >
            <ListItemIcon>
              <BarChartIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Report</ListItemText>
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
}
